import { format, formatDistance, formatRelative } from 'date-fns'
import type { Locale } from 'date-fns'
import { enUS, nl, de, fr, es, it } from 'date-fns/locale'
import { getLocale } from '@/i18n'

/**
 * Map of i18n locale codes to date-fns locale objects
 */
const dateFnsLocales: Record<string, Locale> = {
  'en-US': enUS,
  'nl-NL': nl,
  'de-DE': de,
  'fr-FR': fr,
  'es-ES': es,
  'it-IT': it,
  // Fallbacks for base locales
  en: enUS,
  nl: nl,
  de: de,
  fr: fr,
  es: es,
  it: it,
}

/**
 * Get the date-fns locale object for the current i18n locale
 */
export function getDateFnsLocale(): Locale {
  const currentLocale = getLocale()
  return dateFnsLocales[currentLocale] || dateFnsLocales['en-US'] || enUS
}

/**
 * Format a date using the current locale
 *
 * @param date - The date to format
 * @param formatStr - The format string (date-fns format)
 * @returns The formatted date string
 */
export function formatDate(date: Date | number | string, formatStr: string = 'PP'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return format(dateObj, formatStr, { locale: getDateFnsLocale() })
}

/**
 * Format a date as a relative time string (e.g., "2 days ago")
 *
 * @param date - The date to format
 * @param baseDate - The base date to compare against (defaults to now)
 * @returns The relative time string
 */
export function formatDistanceToNow(
  date: Date | number | string,
  baseDate: Date = new Date()
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return formatDistance(dateObj, baseDate, {
    locale: getDateFnsLocale(),
    addSuffix: true,
  })
}

/**
 * Format a date relative to now with smart formatting
 * (e.g., "today at 12:00", "yesterday at 15:00", "last Monday at 10:00")
 *
 * @param date - The date to format
 * @returns The formatted relative date string
 */
export function formatRelativeDate(date: Date | number | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return formatRelative(dateObj, new Date(), { locale: getDateFnsLocale() })
}

/**
 * Format a number using the current locale
 *
 * @param value - The number to format
 * @param options - Intl.NumberFormat options
 * @returns The formatted number string
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  const locale = getLocale()
  return new Intl.NumberFormat(locale, options).format(value)
}

/**
 * Format a currency value using the current locale
 *
 * @param value - The amount to format
 * @param currency - The currency code (e.g., 'EUR', 'USD')
 * @returns The formatted currency string
 */
export function formatCurrency(value: number, currency: string = 'EUR'): string {
  const locale = getLocale()
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value)
}

/**
 * Format a duration in seconds to a human-readable string
 *
 * @param seconds - The duration in seconds
 * @returns The formatted duration string (e.g., "2h 30m", "45m", "30s")
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }

  return `${minutes}m`
}

/**
 * Format a date and time together using the current locale
 *
 * @param date - The date to format
 * @returns The formatted date and time string
 */
export function formatDateTime(date: Date | number | string): string {
  return formatDate(date, 'PPp')
}

/**
 * Format just the time using the current locale
 *
 * @param date - The date to format
 * @returns The formatted time string
 */
export function formatTime(date: Date | number | string): string {
  return formatDate(date, 'p')
}

/**
 * Parse a UTC timestamp string, ensuring it's treated as UTC.
 * Backend timestamps are stored as UTC but may not have a timezone suffix.
 *
 * @param timestamp - The timestamp string from the backend
 * @returns A Date object representing the UTC time
 */
export function parseUTCTimestamp(timestamp: string): Date {
  // If the timestamp doesn't have a timezone indicator, append 'Z' to treat it as UTC
  if (
    !timestamp.endsWith('Z') &&
    !timestamp.includes('+') &&
    !timestamp.includes('-', 10) // Check for offset (ignoring the hyphen in date portion)
  ) {
    return new Date(timestamp + 'Z')
  }
  return new Date(timestamp)
}

/**
 * Format a date in a specific timezone using Intl.DateTimeFormat.
 * This is useful for displaying times in the assistant's timezone.
 *
 * @param date - The date to format (string, Date, or number)
 * @param timezone - The IANA timezone (e.g., 'Europe/Amsterdam')
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns The formatted date string in the specified timezone
 */
export function formatDateInTimezone(
  date: Date | number | string,
  timezone?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const locale = getLocale()
  const dateObj = typeof date === 'string' ? parseUTCTimestamp(date) : date

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }

  // Only add timeZone if a valid timezone is provided
  if (timezone) {
    defaultOptions.timeZone = timezone
  }

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj)
}

/**
 * Format a date and time in a specific timezone, matching the v1 frontend format.
 *
 * @param date - The date to format (string from backend, Date, or number)
 * @param timezone - The IANA timezone (e.g., 'Europe/Amsterdam')
 * @returns The formatted date string (e.g., "Jan 9, 2026, 11:00 AM")
 */
export function formatConversationDateTime(
  date: Date | number | string,
  timezone?: string
): string {
  return formatDateInTimezone(date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Get a localized weekday name using Intl.DateTimeFormat.
 *
 * @param dayNumber - ISO weekday: 1=Monday ... 7=Sunday
 * @param weekdayFormat - 'short' (Mon) or 'long' (Monday)
 * @returns The localized weekday name
 */
export function getWeekdayName(
  dayNumber: number,
  weekdayFormat: 'short' | 'long' = 'short'
): string {
  const refMonday = new Date(2024, 0, 1) // Jan 1 2024 is a Monday
  const date = new Date(refMonday)
  date.setDate(refMonday.getDate() + (dayNumber - 1))
  return new Intl.DateTimeFormat(getLocale(), { weekday: weekdayFormat }).format(date)
}
